import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, BarChart, Brain, Target, MessageSquare, Settings, 
  TrendingUp, UserPlus, Clock, AlertTriangle, DollarSign,
  BookOpen, Zap, Bell, Shield, Database, LayoutGrid, ChevronRight,
  ArrowUpRight, ArrowDownRight, Sparkles, Search, Filter, Download,
  Edit, Trash, Eye, Plus, RefreshCw, HelpCircle, FileText, 
  CreditCard, Tag, PieChart, UserCheck, UserX, Mail, Phone,
  Calendar, CheckCircle, XCircle, LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Import components
import UsersManagement from '../components/admin/UsersManagement';
import AIManagement from '../components/admin/AIManagement';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';
import MonetizationPanel from '../components/admin/MonetizationPanel';
import ContentManagement from '../components/admin/ContentManagement';
import SupportPanel from '../components/admin/SupportPanel';
import SettingsPanel from '../components/admin/SettingsPanel';
import FeedbackPanel from '../components/admin/FeedbackPanel';

const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { signOut } = useAuth();
  const navigate = useNavigate();

  // Datos de ejemplo para las métricas
  const metrics = {
    users: {
      total: 12458,
      active: 8934,
      growth: 23.5,
      retention: 87.2
    },
    revenue: {
      monthly: 45890,
      growth: 18.7,
      arpu: 39.99
    },
    engagement: {
      dailyActive: 3245,
      weeklyActive: 7890,
      averageTime: 28
    },
    tasks: {
      completed: 34567,
      success: 92.3
    }
  };

  // Datos de ejemplo para actividad reciente
  const recentActivity = [
    {
      id: 1,
      type: 'user_signup',
      icon: <UserPlus className="h-5 w-5 text-green-500" />,
      message: '15 nuevos usuarios se registraron en la última hora',
      time: 'Hace 5 minutos'
    },
    {
      id: 2,
      type: 'task_completion',
      icon: <Target className="h-5 w-5 text-blue-500" />,
      message: '234 tareas completadas hoy',
      time: 'Hace 15 minutos'
    },
    {
      id: 3,
      type: 'alert',
      icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
      message: 'Alerta de rendimiento en el módulo de IA',
      time: 'Hace 30 minutos'
    },
    {
      id: 4,
      type: 'payment',
      icon: <DollarSign className="h-5 w-5 text-green-500" />,
      message: '5 nuevas suscripciones al plan Premium',
      time: 'Hace 45 minutos'
    },
    {
      id: 5,
      type: 'support',
      icon: <MessageSquare className="h-5 w-5 text-purple-500" />,
      message: '3 nuevos tickets de soporte pendientes',
      time: 'Hace 1 hora'
    }
  ];

  // Datos de ejemplo para acciones rápidas
  const quickActions = [
    {
      id: 1,
      icon: <Brain className="h-6 w-6 text-accent" />,
      title: 'Configuración de IA',
      description: 'Ajusta los parámetros del mentor IA y actualiza las respuestas del sistema.',
      action: 'Configurar',
      onClick: () => setActiveTab('ai')
    },
    {
      id: 2,
      icon: <BookOpen className="h-6 w-6 text-accent" />,
      title: 'Gestión de Contenido',
      description: 'Actualiza tareas, desafíos y material de aprendizaje.',
      action: 'Gestionar',
      onClick: () => setActiveTab('content')
    },
    {
      id: 3,
      icon: <Shield className="h-6 w-6 text-accent" />,
      title: 'Seguridad',
      description: 'Revisa logs de seguridad y gestiona permisos de usuario.',
      action: 'Revisar',
      onClick: () => setActiveTab('settings')
    },
    {
      id: 4,
      icon: <DollarSign className="h-6 w-6 text-accent" />,
      title: 'Monetización',
      description: 'Gestiona planes de suscripción y promociones.',
      action: 'Gestionar',
      onClick: () => setActiveTab('monetization')
    },
    {
      id: 5,
      icon: <Users className="h-6 w-6 text-accent" />,
      title: 'Usuarios',
      description: 'Administra usuarios, roles y permisos.',
      action: 'Administrar',
      onClick: () => setActiveTab('users')
    },
    {
      id: 6,
      icon: <MessageSquare className="h-6 w-6 text-accent" />,
      title: 'Soporte',
      description: 'Gestiona tickets de soporte y consultas de usuarios.',
      action: 'Gestionar',
      onClick: () => setActiveTab('support')
    }
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/admin" className="flex items-center">
                  <Brain className="h-8 w-8 text-accent" />
                  <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    Panel de Administración
                  </span>
                </Link>
              </div>
              <div className="hidden md:ml-6 md:flex md:space-x-8">
                <Link to="/" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-foreground/70 hover:text-foreground">
                  Ir al sitio
                </Link>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-foreground/50" />
                </div>
              </div>
              <button className="text-foreground/70 hover:text-foreground relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
              </button>
              <button className="text-foreground/70 hover:text-foreground">
                <Settings className="h-5 w-5" />
              </button>
              <div className="border-l border-border h-6 mx-2"></div>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                  A
                </div>
                <div className="ml-2 hidden md:block">
                  <p className="text-sm font-medium text-foreground">Admin</p>
                </div>
                <button 
                  onClick={handleSignOut}
                  className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:bg-accent/90"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row lg:space-x-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0 mb-8 lg:mb-0">
            <nav className="space-y-1 sticky top-24">
              <button
                onClick={() => setActiveTab('overview')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'overview'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <LayoutGrid className="h-5 w-5 mr-3" />
                Vista General
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'users'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Users className="h-5 w-5 mr-3" />
                Usuarios
              </button>
              <button
                onClick={() => setActiveTab('ai')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'ai'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Brain className="h-5 w-5 mr-3" />
                Mentor IA
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'analytics'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <BarChart className="h-5 w-5 mr-3" />
                Analíticas
              </button>
              <button
                onClick={() => setActiveTab('monetization')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'monetization'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <DollarSign className="h-5 w-5 mr-3" />
                Monetización
              </button>
              <button
                onClick={() => setActiveTab('content')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'content'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <BookOpen className="h-5 w-5 mr-3" />
                Contenido
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'support'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <MessageSquare className="h-5 w-5 mr-3" />
                Soporte
              </button>
              <button
                onClick={() => setActiveTab('feedback')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'feedback'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <MessageSquare className="h-5 w-5 mr-3" />
                Feedback
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'settings'
                    ? 'bg-accent text-white'
                    : 'text-foreground/70 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Settings className="h-5 w-5 mr-3" />
                Configuración
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h1 className="text-2xl font-bold text-foreground">Panel de Control</h1>
                  <div className="flex items-center space-x-2">
                    <button className="flex items-center text-sm text-foreground/70 hover:text-foreground">
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Actualizar
                    </button>
                    <div className="border-l border-border h-6 mx-2"></div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-foreground/70">Periodo:</span>
                      <select className="text-sm border border-border rounded-md bg-background px-2 py-1">
                        <option>Últimos 7 días</option>
                        <option>Últimos 30 días</option>
                        <option>Este mes</option>
                        <option>Este año</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="dark-card p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-foreground/70">Usuarios Activos</p>
                        <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.users.active.toLocaleString()}</h3>
                      </div>
                      <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
                        <ArrowUpRight className="h-5 w-5" />
                        <span className="ml-1">{metrics.users.growth}%</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground/70">Retención</span>
                        <span className="text-green-600">{metrics.users.retention}%</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: `${metrics.users.retention}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="dark-card p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-foreground/70">Ingresos Mensuales</p>
                        <h3 className="text-2xl font-bold text-foreground mt-1">€{metrics.revenue.monthly.toLocaleString()}</h3>
                      </div>
                      <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
                        <ArrowUpRight className="h-5 w-5" />
                        <span className="ml-1">{metrics.revenue.growth}%</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground/70">ARPU</span>
                        <span className="text-green-600">€{metrics.revenue.arpu}</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: '75%' }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="dark-card p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-foreground/70">Tareas Completadas</p>
                        <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.tasks.completed.toLocaleString()}</h3>
                      </div>
                      <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
                        <Target className="h-5 w-5" />
                        <span className="ml-1">{metrics.tasks.success}%</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground/70">Tasa de éxito</span>
                        <span className="text-green-600">{metrics.tasks.success}%</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: `${metrics.tasks.success}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="dark-card p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-foreground/70">Usuarios DAU</p>
                        <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.engagement.dailyActive.toLocaleString()}</h3>
                      </div>
                      <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
                        <Clock className="h-5 w-5" />
                        <span className="ml-1">{metrics.engagement.averageTime}m</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground/70">WAU</span>
                        <span className="text-green-600">{metrics.engagement.weeklyActive.toLocaleString()}</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: '80%' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="dark-card p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-foreground">Actividad Reciente</h3>
                    <button className="text-sm text-accent hover:text-accent/90">Ver todo</button>
                  </div>
                  <div className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-start">
                        <div className="flex-shrink-0">
                          {activity.icon}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-foreground">
                            <span className="font-medium">{activity.message}</span>
                          </p>
                          <p className="text-xs text-foreground/70 mt-1">{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <button className="text-accent hover:text-accent/90 text-sm font-medium flex items-center">
                      Ver toda la actividad
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Acciones Rápidas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {quickActions.map((action) => (
                      <div 
                        key={action.id} 
                        className="dark-card p-6 hover:border-accent/50 transition-all duration-300 cursor-pointer"
                        onClick={action.onClick}
                      >
                        <div className="flex items-center mb-4">
                          <div className="bg-accent/10 p-2 rounded-lg">
                            {action.icon}
                          </div>
                          <h3 className="ml-3 text-lg font-medium text-foreground">{action.title}</h3>
                        </div>
                        <p className="text-sm text-foreground/70 mb-4">
                          {action.description}
                        </p>
                        <button className="text-accent hover:text-accent/90 text-sm font-medium flex items-center">
                          {action.action}
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && <UsersManagement />}
            {activeTab === 'ai' && <AIManagement />}
            {activeTab === 'analytics' && <AnalyticsPanel />}
            {activeTab === 'monetization' && <MonetizationPanel />}
            {activeTab === 'content' && <ContentManagement />}
            {activeTab === 'support' && <SupportPanel />}
            {activeTab === 'settings' && <SettingsPanel />}
            {activeTab === 'feedback' && <FeedbackPanel />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;